import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MarketplaceCard from '../components/MarketplaceCard';
import { fetchMarketplaceListings, type MarketplaceListing } from '../lib/marketplace-api';
import { MARKETPLACE_CATEGORIES, NZ_REGIONS } from '../lib/marketplace-categories';
import { isLoggedIn } from '../lib/marketplace-auth';

const PAGE_SIZE = 20;

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const region = searchParams.get('region') || '';
  const search = searchParams.get('search') || '';
  const condition = searchParams.get('condition') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    fetchMarketplaceListings({
      category: category || undefined,
      subcategory: subcategory || undefined,
      region: region || undefined,
      condition: condition || undefined,
      search: search || undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((data) => {
        setListings(data.listings);
        setTotalPages(data.pagination.totalPages);
        setTotal(data.pagination.total);
      })
      .catch(() => {
        setListings([]);
        setTotalPages(1);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [category, subcategory, region, condition, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParams = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    // Reset to page 1 when filters change (unless explicitly setting page)
    if (!('page' in updates)) next.delete('page');
    setSearchParams(next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ search: searchInput.trim() });
  };

  const activeCategory = MARKETPLACE_CATEGORIES.find((c) => c.value === category);

  return (
    <div>
      {/* Hero header */}
      <section className="relative bg-[#0A1929] text-white py-14 md:py-20 px-4 md:px-8 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(79,179,217,0.18), transparent 60%)' }}
        />
        <div className="max-w-[1440px] mx-auto relative">
          <h1 className="font-display text-3xl md:text-[2.75rem] font-extrabold tracking-tight mb-3">
            Marine Marketplace
          </h1>
          <p className="text-white/70 text-sm md:text-lg max-w-[560px] mb-8">
            Buy &amp; sell boats, gear &amp; equipment across New Zealand. From trailer boats to tackle - listed directly by Kiwi boaties.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-[560px]">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search boats, motors, gear…"
              className="flex-1 h-11 px-4 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm outline-none focus:border-accent backdrop-blur"
            />
            <button type="submit" className="btn btn-primary h-11 px-5">
              Search
            </button>
          </form>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={isLoggedIn() ? '/marketplace/create' : '/marketplace/signin'}
              className="btn btn-primary btn-sm"
            >
              + Sell something
            </Link>
            {isLoggedIn() && (
              <Link to="/marketplace/dashboard" className="btn btn-ghost btn-sm !text-white/80">
                My listings
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 mb-5">
          <button
            onClick={() => updateParams({ category: '', subcategory: '' })}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              !category
                ? 'bg-accent text-white border-accent'
                : 'bg-card-light dark:bg-card border-[rgba(79,179,217,0.2)] text-text-secondary-light dark:text-text-secondary hover:border-accent/50'
            }`}
          >
            All
          </button>
          {MARKETPLACE_CATEGORIES.map((c) => (
            <button
              key={c.value}
              onClick={() => updateParams({ category: c.value, subcategory: '' })}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                category === c.value
                  ? 'bg-accent text-white border-accent'
                  : 'bg-card-light dark:bg-card border-[rgba(79,179,217,0.2)] text-text-secondary-light dark:text-text-secondary hover:border-accent/50'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Subcategory + region + condition filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {activeCategory && (
            <select
              value={subcategory}
              onChange={(e) => updateParams({ subcategory: e.target.value })}
              className="form-input !w-auto text-sm"
            >
              <option value="">All {activeCategory.label}</option>
              {activeCategory.subcategories.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          <select
            value={region}
            onChange={(e) => updateParams({ region: e.target.value })}
            className="form-input !w-auto text-sm"
          >
            <option value="">All regions</option>
            {NZ_REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select
            value={condition}
            onChange={(e) => updateParams({ condition: e.target.value })}
            className="form-input !w-auto text-sm"
          >
            <option value="">Any condition</option>
            <option value="new">New</option>
            <option value="used">Used</option>
            <option value="refurbished">Refurbished</option>
            <option value="parts">Parts</option>
          </select>
          {(category || region || search || condition || subcategory) && (
            <button
              onClick={() => setSearchParams(new URLSearchParams())}
              className="text-sm text-accent hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-surface-light dark:bg-surface" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-1/3 bg-surface-light dark:bg-surface rounded" />
                  <div className="h-4 w-full bg-surface-light dark:bg-surface rounded" />
                  <div className="h-4 w-1/2 bg-surface-light dark:bg-surface rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⚓</div>
            <h2 className="font-display text-xl font-bold mb-2">No listings found</h2>
            <p className="text-text-secondary text-sm mb-6">
              {search || category || region
                ? 'Try widening your search or clearing the filters.'
                : 'Be the first to list something for sale!'}
            </p>
            <Link
              to={isLoggedIn() ? '/marketplace/create' : '/marketplace/signin'}
              className="btn btn-primary"
            >
              List an item for sale
            </Link>
          </div>
        ) : (
          <>
            <p className="text-text-muted text-xs mb-4">
              {total} listing{total === 1 ? '' : 's'} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {listings.map((l) => <MarketplaceCard key={l.id} listing={l} />)}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <button
                  disabled={page <= 1}
                  onClick={() => updateParams({ page: String(page - 1) })}
                  className="btn btn-ghost btn-sm disabled:opacity-40"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => updateParams({ page: String(p) })}
                      className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? 'bg-accent text-white'
                          : 'text-text-secondary-light dark:text-text-secondary hover:bg-accent-glow'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                {totalPages > 7 && <span className="text-text-muted text-sm">… {totalPages}</span>}
                <button
                  disabled={page >= totalPages}
                  onClick={() => updateParams({ page: String(page + 1) })}
                  className="btn btn-ghost btn-sm disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
